import { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import {
  activeSurface,
  initialSurfaceCounts,
  surfaceReducer,
  type Surface,
} from "@/lib/surface";

interface SurfaceContextValue {
  /** The surface currently held, or null. */
  surface: Surface | null;
  /** Claim a surface; call the returned function to release it. */
  enterSurface: (surface: Surface) => () => void;
}

const SurfaceContext = createContext<SurfaceContextValue | null>(null);

/** Outermost provider (above ThemeProvider in main.tsx) so both RootLayout and
 * ThemeProvider can read the surface. Nothing is stamped on <html> — the scope
 * is the page column plus route-owned portal roots, so the app chrome keeps its
 * own theme. */
export function SurfaceProvider({ children }: { children: React.ReactNode }) {
  const [counts, dispatch] = useReducer(surfaceReducer, initialSurfaceCounts);

  const enterSurface = useCallback((surface: Surface) => {
    dispatch({ type: "enter", surface });
    let left = false;
    // Leave exactly once, however many times the cleanup runs (StrictMode
    // double-invokes effects, and a stray second call would unbalance the
    // ref count).
    return () => {
      if (left) return;
      left = true;
      dispatch({ type: "leave", surface });
    };
  }, []);

  const value = useMemo(
    () => ({ surface: activeSurface(counts), enterSurface }),
    [counts, enterSurface],
  );

  return <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>;
}

export function useSurface(): SurfaceContextValue {
  const ctx = useContext(SurfaceContext);
  if (!ctx) throw new Error("useSurface must be used within SurfaceProvider");
  return ctx;
}
